import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    TextField,
    Button,
    Checkbox,
    FormControlLabel,
    Radio,
    RadioGroup,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Box,
    Typography,
    List,
    ListItem,
    useTheme,
    useMediaQuery
} from '@mui/material';
import { getDaysInMonth, format } from 'date-fns';
import { DrugExceptionsClient } from '../../../client/drug-exceptions-client';
import { Gender, Province, PreferredContactMethod, RelationshipType } from '../../../model/Reformulary';
import GetLangConfig from '../../../util/LangUtil';
import './register.css';

const logFieldChange = (fieldName, newValue , context = {}) => {
    console.log('Field Change:', {
        field: fieldName,
        newValue,
        timestamp: new Date().toISOString(),
        ...context
    });
};

const logFormState = (formState, context = {}) => {
    console.log('Form State:', {
        ...formState,
        timestamp: new Date().toISOString(),
        ...context
    });
};

function Register({ handleError }) {
    const navigate = useNavigate();
    const langConfig = GetLangConfig('register');
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    const [form, setForm] = useState({
        firstName: '',
        lastName: '',
        memberFirstName: '',
        memberLastName: '',
        gender: '',
        genderOther: '',
        address1: '',
        address2: '',
        city: '',
        postalCode: '',
        phoneNumber: '',
        email: '',
        password: '',
        password2: '',
        policyNumber: '',
        certificateNumber: '',
        employerNumber: '',
        preferredMethodOfContact: '',
        relationshipType: '',
        reviewed: false
    });

    const [errors, setErrors] = useState({
        firstName: false,
        lastName: false,
        memberFirstName: false,
        memberLastName: false,
        gender: false,
        address1: false,
        city: false,
        postalCode: false,
        phoneNumber: false,
        email: false,
        password: false,
        password2: false,
        policyNumber: false,
        certificateNumber: false,
        employerNumber: false,
        preferredMethodOfContact: false,
        relationshipType: false,
        reviewed: false
    });

    const [dateOfBirth, setDateOfBirthState] = useState(() => {
        const date = new Date();
        date.setUTCFullYear(date.getUTCFullYear() - 18);
        return date;
    });
    const [province, setProvince] = useState('');
    const [currentStep, setCurrentStep] = useState(1);
    const [passwordError, setPasswordError] = useState('');
    const [daysInMonth, setDaysInMonth] = useState(31);
    const [provinceError, setProvinceError] = useState(false);
    const [dateOfBirthError, setDateOfBirthError] = useState({
        year: false,
        month: false,
        day: false
    });
    const [yearInput, setYearInput] = useState('');

    useEffect(() => {
        setYearInput(dateOfBirth.getUTCFullYear().toString());
    }, []);

    const StepIndicator = ({ currentStep }) => {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                {[1, 2, 3].map((step) => (
                    <Box
                        key={step}
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            bgcolor: currentStep >= step ? 'primary.main' : 'grey.300',
                            color: currentStep >= step ? 'common.white' : 'text.primary',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mx: 1,
                            fontWeight: 'bold'
                        }}
                    >
                        {step}
                    </Box>
                ))}
            </Box>
        );
    };

    const setDateOfBirth = (type, value) => {
        logFieldChange(`dateOfBirth.${type}`, value, {
            previousValue: type === 'year'
                ? dateOfBirth?.getUTCFullYear()
                : type === 'month'
                    ? (dateOfBirth?.getUTCMonth() || 0) + 1
                    : dateOfBirth?.getUTCDate()
        });

        const currentDate = new Date(dateOfBirth);
        let newDate;

        if (type === 'year') {
            setYearInput(value);
            if (value === '') return;
            const yearValue = parseInt(value);
            if (isNaN(yearValue)) return;
            newDate = new Date(
                yearValue,
                currentDate.getUTCMonth(),
                currentDate.getUTCDate()
            );
        } else if (type === 'month') {
            newDate = new Date(
                currentDate.getUTCFullYear(),
                value - 1,
                currentDate.getUTCDate()
            );
        } else if (type === 'date') {
            newDate = new Date(
                currentDate.getUTCFullYear(),
                currentDate.getUTCMonth(),
                value
            );
        }

        setDateOfBirthState(newDate);

        if (type === 'month' || type === 'year') {
            const days = getDaysInMonth(newDate);
            setDaysInMonth(days);
            if (newDate.getUTCDate() > days) {
                newDate.setUTCDate(1);
                setDateOfBirthState(newDate);
            }
        }

        logFormState(form, { dateOfBirth: newDate });
    };

    const validateDateField = (type, value) => {
        let isValid = true;
        let errorState = { ...dateOfBirthError };

        if (value === '' || value === null || value === undefined) {
            isValid = false;
            errorState[type] = true;
        } else {
            const numericValue = parseInt(value);

            if (type === 'year') {
                if (isNaN(numericValue) || numericValue < 1900 || numericValue > new Date().getUTCFullYear()) {
                    isValid = false;
                    errorState.year = true;
                } else {
                    errorState.year = false;
                }
            } else if (type === 'month') {
                if (isNaN(numericValue) || numericValue < 1 || numericValue > 12) {
                    isValid = false;
                    errorState.month = true;
                } else {
                    errorState.month = false;
                }
            } else if (type === 'day') {
                if (isNaN(numericValue) || numericValue < 1 || numericValue > daysInMonth) {
                    isValid = false;
                    errorState.day = true;
                } else {
                    errorState.day = false;
                }
            }
        }

        setDateOfBirthError(errorState);
        return isValid;
    };

    const handleDateFieldBlur = (type) => {
        if (type === 'year') {
            validateDateField('year', yearInput);
        } else if (type === 'month') {
            validateDateField('month', (dateOfBirth?.getUTCMonth() || 0) + 1);
        } else if (type === 'day') {
            validateDateField('day', dateOfBirth?.getUTCDate());
        }
    };

    const validateField = (fieldName, value) => {
        let isValid = true;

        if (value === '' || value === null || value === undefined) {
            isValid = false;
        }

        if (fieldName === 'email' && value) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            isValid = emailRegex.test(value);
        }

        if (fieldName === 'postalCode' && value) {
            const postalCodeRegex = /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/;
            isValid = postalCodeRegex.test(value);
        }

        if (fieldName === 'phoneNumber' && value) {
            const phoneRegex = /^\d{3}-\d{3}-\d{4}$/;
            isValid = phoneRegex.test(value);
        }

        setErrors(prev => ({
            ...prev,
            [fieldName]: !isValid
        }));

        return isValid;
    };

    const shouldValidateFieldInStep = (fieldName, step) => {
        const stepFields = {
            2: ['memberFirstName', 'memberLastName', 'policyNumber', 'certificateNumber', 'employerNumber', 'reviewed'],
            3: ['firstName', 'lastName', 'gender', 'address1', 'city', 'postalCode', 'phoneNumber', 'email', 'password', 'password2', 'preferredMethodOfContact', 'relationshipType'],
            4: []
        };
        return stepFields[step]?.includes(fieldName) || false;
    };

    const validateStep = (step) => {
        let isValid = true;
        const newErrors = { ...errors };

        // Reset errors for current step
        Object.keys(newErrors).forEach(key => {
            if (shouldValidateFieldInStep(key, step)) {
                newErrors[key] = false;
            }
        });


        const isDateValid =
            validateDateField('year', yearInput) &&
            validateDateField('month', (dateOfBirth?.getUTCMonth() || 0) + 1) &&
            validateDateField('day', dateOfBirth?.getUTCDate());

        if (step === 2) {
            if (!form.memberFirstName) {
                newErrors.memberFirstName = true;
                isValid = false;
            }
            if (!form.memberLastName) {
                newErrors.memberLastName = true;
                isValid = false;
            }
            if (!form.policyNumber) {
                newErrors.policyNumber = true;
                isValid = false;
            }
            if (!form.certificateNumber) {
                newErrors.certificateNumber = true;
                isValid = false;
            }
            if (!form.employerNumber) {
                newErrors.employerNumber = true;
                isValid = false;
            }
            if (!form.reviewed) {
                newErrors.reviewed = true;
                isValid = false;
            }
        } else if (step === 3) {
            if (!form.firstName) {
                newErrors.firstName = true;
                isValid = false;
            }
            if (!form.lastName) {
                newErrors.lastName = true;
                isValid = false;
            }
            if (!form.gender) {
                newErrors.gender = true;
                isValid = false;
            }
            if (!form.address1) {
                newErrors.address1 = true;
                isValid = false;
            }
            if (!form.city) {
                newErrors.city = true;
                isValid = false;
            }
            if (!form.postalCode) {
                newErrors.postalCode = true;
                isValid = false;
            }
            if (!province) {
                setProvinceError(true);
                isValid = false;
            }
            if (!isDateValid) {
                isValid = false;
            }
            if (!form.phoneNumber) {
                newErrors.phoneNumber = true;
                isValid = false;
            }
            if (!form.email) {
                newErrors.email = true;
                isValid = false;
            }
            if (!form.password) {
                newErrors.password = true;
                isValid = false;
            }
            if (!form.password2 || form.password !== form.password2) {
                newErrors.password2 = true;
                isValid = false;
            }
            if (!form.preferredMethodOfContact) {
                newErrors.preferredMethodOfContact = true;
                isValid = false;
            }
            if (!form.relationshipType) {
                newErrors.relationshipType = true;
                isValid = false;
            }
        }

        setErrors(newErrors);
        return isValid;
    };

    const handlePostalCode = (e) => {
        const value = e.target.value.replace(/[^A-Za-z0-9]/gi, '').toUpperCase();
        logFieldChange('postalCode', value, { previousValue: form.postalCode });
        const newForm = { ...form, postalCode: value };
        setForm(newForm);
        validateField('postalCode', value);
        logFormState(newForm);
    };

    const handleCertificateNumber = (e) => {
        if (/^[a-zA-Z0-9]*$/.test(e.target.value)) {
            const value = e.target.value.toUpperCase();
            logFieldChange('certificateNumber', value, { previousValue: form.certificateNumber });
            const newForm = { ...form, certificateNumber: value };
            setForm(newForm);
            validateField('certificateNumber', value);
            logFormState(newForm);
        }
    };

    const formatPhoneNumber = (value) => {
        if (!value) return value;
        const phoneNumber = value.replace(/[^\d]/g, '');
        const phoneNumberLength = phoneNumber.length;

        if (phoneNumberLength < 4) return phoneNumber;
        if (phoneNumberLength < 7) {
            return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3)}`;
        }
        return `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    };

    const handlePhoneNumber = (e) => {
        const formattedPhoneNumber = formatPhoneNumber(e.target.value);
        logFieldChange('phoneNumber', formattedPhoneNumber, { previousValue: form.phoneNumber });
        const newForm = { ...form, phoneNumber: formattedPhoneNumber };
        setForm(newForm);
        validateField('phoneNumber', formattedPhoneNumber);
        logFormState(newForm);
    };

    const handleNextStep = () => {
        const isValid = validateStep(currentStep);
        if (isValid) {
            setCurrentStep(currentStep + 1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const handlePrevStep = () => {
        setCurrentStep(currentStep - 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSubmit = async () => {
        const isStep2Valid = validateStep(2);
        const isStep3Valid = validateStep(3);
        const isStep4Valid = validateStep(4);

        if (!isStep2Valid || !isStep3Valid || !isStep4Valid) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        console.log('Form submission initiated', {
            formState: form,
            dateOfBirth,
            province,
            timestamp: new Date().toISOString()
        });

        const formattedDate = format(dateOfBirth, 'yyyy-MM-dd');

        const payload = {
            firstName: form.firstName,
            lastName: form.lastName,
            memberFirstName: form.memberFirstName,
            memberLastName: form.memberLastName,
            dateOfBirth: formattedDate,
            gender: form.gender,
            address1: form.address1,
            address: form.address2,
            city: form.city,
            postalCode: form.postalCode,
            province: province,
            phoneNumber: form.phoneNumber,
            email: form.email,
            password: form.password,
            policyNumber: form.policyNumber,
            certificateNumber: form.certificateNumber,
            employerNumber: form.employerNumber,
            preferredMethodOfContact: form.preferredMethodOfContact,
            relationshipType: form.relationshipType,
            accessType: form.relationshipType === RelationshipType.MEMBER ? 'MemberClaimant' : 'SpouseClaimant'
        };

        try {
            const client = new DrugExceptionsClient();
            const response = await client.api.post('/claimants', payload);
            console.log('Form submission successful', {
                response: response.data,
                timestamp: new Date().toISOString()
            });
            sessionStorage.setItem('registrationSuccess', JSON.stringify(response.data));
            navigate('/registrationSuccess');
        } catch (error) {
            console.error('Form submission failed', {
                error,
                formState: form,
                timestamp: new Date().toISOString()
            });
            window.scrollTo({ top: 0, behavior: 'smooth' });
            handleError(error);
        }
    };

    useEffect(() => {
        if (form.password && form.password2 && form.password !== form.password2) {
            setPasswordError(langConfig.general.fieldInvalid);
            setErrors(prev => ({
                ...prev,
                password2: true
            }));
        } else {
            setPasswordError('');
            setErrors(prev => ({
                ...prev,
                password2: false
            }));
        }
    }, [form.password, form.password2, langConfig.general.fieldInvalid]);

    useEffect(() => {
        setDaysInMonth(getDaysInMonth(dateOfBirth));
    }, [dateOfBirth]);

    return (
        <div className="register-container">
            <StepIndicator currentStep={currentStep} />
            {currentStep === 1 && (
                <Box sx={{ width: isSmallScreen ? '100%' : '80%', p: 3 }}>
                    <div className="step-one">
                        <h1>{langConfig.register.header}</h1>
                        <Typography className="header">{langConfig.register.p1}</Typography>
                        <Typography>{langConfig.register.p2}</Typography>
                        <Typography>{langConfig.register.p3}</Typography>
                        <ul>
                            <li>{langConfig.general.policyPlan}</li>
                            <li>{langConfig.general.certificateNumber}</li>
                            <li>{langConfig.general.orgDealerName}</li>
                            <li>{langConfig.general.planMemberName}</li>
                        </ul>
                        <Typography>{langConfig.register.p4}</Typography>
                        <div>
                            <Typography>{langConfig.register.p5}:</Typography>
                            <Typography dangerouslySetInnerHTML={{ __html: langConfig.register.p6 }} />
                        </div>
                        <div>
                            <Typography>{langConfig.register.p7}:</Typography>
                            <div>
                                <Typography>{langConfig.register.p8}</Typography>
                                <Typography>{langConfig.register.p9}</Typography>
                                <Typography>{langConfig.register.p10}</Typography>
                                <Typography>{langConfig.register.p11}</Typography>
                            </div>
                        </div>
                        <div>
                            <Typography>{langConfig.register.p12}:</Typography>
                            <div>
                                <Typography>{langConfig.register.p13}</Typography>
                                <Typography>{langConfig.register.p14}</Typography>
                            </div>
                        </div>
                        <div>
                            <Typography>{langConfig.register.p15}:</Typography>
                            <Typography>{langConfig.register.p16}</Typography>
                        </div>
                        <div>
                            <Typography>{langConfig.register.p17}:</Typography>
                            <div>
                                <Typography dangerouslySetInnerHTML={{ __html: langConfig.register.p18 }} />
                                <Typography>{langConfig.register.p19}</Typography>
                                <Typography>{langConfig.register.p20}</Typography>
                            </div>
                        </div>
                        <div>
                            <Typography>{langConfig.register.p21}:</Typography>
                            <div>
                                <Typography>{langConfig.register.p22}</Typography>
                                <Typography>{langConfig.register.p23}</Typography>
                                <Typography>{langConfig.register.p24}</Typography>
                            </div>
                        </div>
                        <div>
                            <Typography>{langConfig.register.p25}:</Typography>
                            <div>
                                <Typography>{langConfig.register.p26}</Typography>
                                <Typography>{langConfig.register.p27}</Typography>
                            </div>
                        </div>

                        <Button variant="contained" onClick={handleNextStep}    sx={{
                            mt: 2,
                            ml: 'auto', // Pushes the button to the right
                            display: 'block' // Ensures margin-left works
                        }}>
                            {langConfig.general.next}
                        </Button>
                    </div>
                </Box>
            )}

            {currentStep === 2 && (
                <Box sx={{ width: isSmallScreen ? '100%' : '50%', p: 3 }}>
                    <div className="step-two">
                        <Typography variant="h4">{langConfig.register.header}</Typography>
                        <Typography>{langConfig.register.p1}</Typography>
                        <Typography>{langConfig.register.p2}</Typography>
                        <Typography>{langConfig.register.p3}</Typography>

                        <List sx={{ listStyleType: 'disc', pl: 2 }}>
                            {[
                                langConfig.general.policyPlan,
                                langConfig.general.certificateNumber,
                                langConfig.general.orgDealerName,
                                langConfig.general.planMemberName,
                            ].map((item, i) => (
                                <ListItem key={i} sx={{ display: 'list-item' }}>
                                    <Typography>{item}</Typography>
                                </ListItem>
                            ))}
                        </List>

                        <TextField
                            label={`${langConfig.general.firstname}*`}
                            value={form.memberFirstName}
                            onChange={(e) => {
                                logFieldChange('memberFirstName', e.target.value, { previousValue: form.memberFirstName });
                                const newForm = { ...form, memberFirstName: e.target.value };
                                setForm(newForm);
                                validateField('memberFirstName', e.target.value);
                                logFormState(newForm);
                            }}
                            fullWidth
                            margin="normal"
                            required
                            error={errors.memberFirstName}
                            helperText={errors.memberFirstName ? langConfig.general.fieldInvalid : ''}
                        />

                        <TextField
                            label={`${langConfig.general.lastname}*`}
                            value={form.memberLastName}
                            onChange={(e) => {
                                logFieldChange('memberLastName', e.target.value, { previousValue: form.memberLastName });
                                const newForm = { ...form, memberLastName: e.target.value };
                                setForm(newForm);
                                validateField('memberLastName', e.target.value);
                                logFormState(newForm);
                            }}
                            fullWidth
                            margin="normal"
                            required
                            error={errors.memberLastName}
                            helperText={errors.memberLastName ? langConfig.general.fieldInvalid : ''}
                        />

                        <TextField
                            label={`${langConfig.general.policyPlan}*`}
                            value={form.policyNumber}
                            onChange={(e) => {
                                logFieldChange('policyNumber', e.target.value, { previousValue: form.policyNumber });
                                const newForm = { ...form, policyNumber: e.target.value };
                                setForm(newForm);
                                validateField('policyNumber', e.target.value);
                                logFormState(newForm);
                            }}
                            fullWidth
                            margin="normal"
                            required
                            error={errors.policyNumber}
                            helperText={errors.policyNumber ? langConfig.general.fieldInvalid : ''}
                        />

                        <TextField
                            label={`${langConfig.general.certificateNumber}*`}
                            value={form.certificateNumber}
                            onChange={handleCertificateNumber}
                            fullWidth
                            margin="normal"
                            required
                            error={errors.certificateNumber}
                            helperText={errors.certificateNumber ? langConfig.general.fieldInvalid : ''}
                        />

                        <TextField
                            label={`${langConfig.general.orgDealerName}*`}
                            value={form.employerNumber}
                            onChange={(e) => {
                                logFieldChange('employerNumber', e.target.value, { previousValue: form.employerNumber });
                                const newForm = { ...form, employerNumber: e.target.value };
                                setForm(newForm);
                                validateField('employerNumber', e.target.value);
                                logFormState(newForm);
                            }}
                            fullWidth
                            margin="normal"
                            required
                            error={errors.employerNumber}
                            helperText={errors.employerNumber ? langConfig.general.fieldInvalid : ''}
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={form.reviewed}
                                    onChange={(e) => {
                                        logFieldChange('reviewed', e.target.checked, { previousValue: form.reviewed });
                                        const newForm = { ...form, reviewed: e.target.checked };
                                        setForm(newForm);
                                        validateField('reviewed', e.target.checked);
                                        logFormState(newForm);
                                    }}
                                />
                            }
                            label={
                                <Typography>
                                    {langConfig.register.p31}
                                    <Link to="/termsofservice" style={{ marginLeft: '4px' }}>
                                        {langConfig.register.p32}
                                    </Link>
                                    {langConfig.register.p33}
                                    <Link to="/privacypolicy">{langConfig.register.p34}</Link>
                                    {langConfig.register.p35}
                                </Typography>
                            }
                        />

                        {errors.reviewed && (
                            <Typography color="error" variant="caption">
                                {langConfig.general.fieldInvalid}
                            </Typography>
                        )}

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                            <Button variant="outlined" onClick={handlePrevStep}>
                                {langConfig.general.goback}
                            </Button>
                            <Button variant="contained" onClick={handleNextStep}>
                                {langConfig.general.next}
                            </Button>
                        </Box>
                    </div>
                </Box>
            )}

            {currentStep === 3 && (
                <Box sx={{ width: isSmallScreen ? '100%' : '50%', p: 3 }}>
                    <div className="step-three">
                        <Typography variant="h6">{langConfig.register.p28}</Typography>
                        <Typography>* {langConfig.register.p29}</Typography>
                        <Typography>{langConfig.register.p30}</Typography>

                        <FormControl component="fieldset" sx={{ mt: 2 }} error={errors.relationshipType}>
                            <Typography>{langConfig.register.p39}</Typography>
                            <RadioGroup
                                value={form.relationshipType}
                                onChange={(e) => {
                                    logFieldChange('relationshipType', e.target.value, { previousValue: form.relationshipType });
                                    const newForm = { ...form, relationshipType: e.target.value };
                                    setForm(newForm);
                                    validateField('relationshipType', e.target.value);
                                    logFormState(newForm);
                                }}
                            >
                                <FormControlLabel value="MEMBER" control={<Radio />} label={langConfig.register.p40} />
                                <FormControlLabel value="SPOUSE" control={<Radio />} label={langConfig.register.p41} />
                            </RadioGroup>
                            {errors.relationshipType && (
                                <Typography color="error" variant="caption">
                                    {langConfig.general.fieldInvalid}
                                </Typography>
                            )}
                        </FormControl>

                        <TextField
                            label={`${langConfig.general.firstname}*`}
                            value={form.firstName}
                            onChange={(e) => {
                                logFieldChange('firstName', e.target.value, { previousValue: form.firstName });
                                const newForm = { ...form, firstName: e.target.value };
                                setForm(newForm);
                                validateField('firstName', e.target.value);
                                logFormState(newForm);
                            }}
                            fullWidth
                            margin="normal"
                            required
                            error={errors.firstName}
                            helperText={errors.firstName ? langConfig.general.fieldInvalid : ''}
                        />

                        <TextField
                            label={`${langConfig.general.lastname}*`}
                            value={form.lastName}
                            onChange={(e) => {
                                logFieldChange('lastName', e.target.value, { previousValue: form.lastName });
                                const newForm = { ...form, lastName: e.target.value };
                                setForm(newForm);
                                validateField('lastName', e.target.value);
                                logFormState(newForm);
                            }}
                            fullWidth
                            margin="normal"
                            required
                            error={errors.lastName}
                            helperText={errors.lastName ? langConfig.general.fieldInvalid : ''}
                        />

                        <Typography>{langConfig.register.p42}</Typography>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <TextField
                                label={`${langConfig.general.year}*`}
                                value={yearInput}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (/^\d*$/.test(value) && value.length <= 4) {
                                        setYearInput(value);
                                        setDateOfBirth('year', value);
                                    }
                                }}
                                onBlur={() => handleDateFieldBlur('year')}
                                fullWidth
                                margin="normal"
                                required
                                error={dateOfBirthError.year}
                                helperText={dateOfBirthError.year ? langConfig.general.fieldInvalid : ''}
                            />

                            <FormControl fullWidth margin="normal" error={dateOfBirthError.month}>
                                <InputLabel>{`${langConfig.general.month}*`}</InputLabel>
                                <Select
                                    value={(dateOfBirth?.getUTCMonth() || 0) + 1}
                                    onChange={(e) => {
                                        const month = parseInt(e.target.value);
                                        logFieldChange('dateOfBirth.month', month, {
                                            previousMonth: (dateOfBirth?.getUTCMonth() || 0) + 1,
                                        });
                                        setDateOfBirth('month', month);
                                    }}
                                    onBlur={() => handleDateFieldBlur('month')}
                                    required
                                >
                                    {[...Array(12)].map((_, i) => (
                                        <MenuItem key={i + 1} value={i + 1}>
                                            {langConfig.general[
                                                [
                                                    'january',
                                                    'february',
                                                    'march',
                                                    'april',
                                                    'may',
                                                    'june',
                                                    'july',
                                                    'august',
                                                    'september',
                                                    'october',
                                                    'november',
                                                    'december',
                                                ][i]
                                                ]}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {dateOfBirthError.month && (
                                    <Typography color="error" variant="caption">
                                        {langConfig.general.fieldInvalid}
                                    </Typography>
                                )}
                            </FormControl>

                            <FormControl fullWidth margin="normal" error={dateOfBirthError.day}>
                                <InputLabel>{`${langConfig.general.day}*`}</InputLabel>
                                <Select
                                    value={dateOfBirth?.getUTCDate() || 1}
                                    onChange={(e) => {
                                        const day = parseInt(e.target.value);
                                        logFieldChange('dateOfBirth.day', day, { previousDay: dateOfBirth?.getUTCDate() });
                                        setDateOfBirth('date', day);
                                    }}
                                    onBlur={() => handleDateFieldBlur('day')}
                                    required
                                >
                                    {[...Array(daysInMonth)].map((_, i) => (
                                        <MenuItem key={i + 1} value={i + 1}>
                                            {i + 1}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {dateOfBirthError.day && (
                                    <Typography color="error" variant="caption">
                                        {langConfig.general.fieldInvalid}
                                    </Typography>
                                )}
                            </FormControl>
                        </Box>

                        <FormControl component="fieldset" sx={{ mt: 2 }} error={errors.gender}>
                            <Typography>{`${langConfig.general.gender}*`}</Typography>
                            <RadioGroup
                                value={form.gender}
                                onChange={(e) => {
                                    logFieldChange('gender', e.target.value, { previousValue: form.gender });
                                    const newForm = { ...form, gender: e.target.value };
                                    setForm(newForm);
                                    validateField('gender', e.target.value);
                                    logFormState(newForm);
                                }}
                            >
                                <FormControlLabel value="MALE" control={<Radio />} label={langConfig.register.p43} />
                                <FormControlLabel value="FEMALE" control={<Radio />} label={langConfig.register.p44} />
                                <FormControlLabel value="OTHER" control={<Radio />} label={langConfig.register.p45} />
                            </RadioGroup>
                            {errors.gender && (
                                <Typography color="error" variant="caption">
                                    {langConfig.general.fieldInvalid}
                                </Typography>
                            )}
                        </FormControl>

                        <TextField
                            label={`${langConfig.register.p46}*`}
                            value={form.address1}
                            onChange={(e) => {
                                logFieldChange('address1', e.target.value, { previousValue: form.address1 });
                                const newForm = { ...form, address1: e.target.value };
                                setForm(newForm);
                                validateField('address1', e.target.value);
                                logFormState(newForm);
                            }}
                            fullWidth
                            margin="normal"
                            required
                            error={errors.address1}
                            helperText={errors.address1 ? langConfig.general.fieldInvalid : ''}
                        />

                        <TextField
                            label={`${langConfig.register.p46}`}
                            value={form.address2}
                            onChange={(e) => {
                                logFieldChange('address2', e.target.value, { previousValue: form.address2 });
                                const newForm = { ...form, address2: e.target.value };
                                setForm(newForm);
                                logFormState(newForm);
                            }}
                            fullWidth
                            margin="normal"
                        />

                        <TextField
                            label={`${langConfig.register.p47}*`}
                            value={form.city}
                            onChange={(e) => {
                                logFieldChange('city', e.target.value, { previousValue: form.city });
                                const newForm = { ...form, city: e.target.value };
                                setForm(newForm);
                                validateField('city', e.target.value);
                                logFormState(newForm);
                            }}
                            fullWidth
                            margin="normal"
                            required
                            error={errors.city}
                            helperText={errors.city ? langConfig.general.fieldInvalid : ''}
                        />

                        <FormControl fullWidth margin="normal" error={provinceError}>
                            <InputLabel>{`${langConfig.register.p48}*`}</InputLabel>
                            <Select
                                value={province}
                                onChange={(e) => {
                                    logFieldChange('province', e.target.value, { previousValue: province });
                                    setProvince(e.target.value);
                                    setProvinceError(false);
                                    logFormState(form, { province: e.target.value });
                                }}
                                required
                            >
                                <MenuItem key={Province[Province.NL]} value={Province[Province.NL]}>{langConfig.general.provinces.nfld}</MenuItem>
                                <MenuItem key={Province[Province.PE]} value={Province[Province.PE]}>{langConfig.general.provinces.pei}</MenuItem>
                                <MenuItem key={Province[Province.NS]} value={Province[Province.NS]}>{langConfig.general.provinces.ns}</MenuItem>
                                <MenuItem key={Province[Province.NB]} value={Province[Province.NB]}>{langConfig.general.provinces.nb}</MenuItem>
                                <MenuItem key={Province[Province.QC]} value={Province[Province.QC]}>{langConfig.general.provinces.qbc}</MenuItem>
                                <MenuItem key={Province[Province.ON]} value={Province[Province.ON]}>{langConfig.general.provinces.on}</MenuItem>
                                <MenuItem key={Province[Province.MB]} value={Province[Province.MB]}>{langConfig.general.provinces.mb}</MenuItem>
                                <MenuItem key={Province[Province.SK]} value={Province[Province.SK]}>{langConfig.general.provinces.sk}</MenuItem>
                                <MenuItem key={Province[Province.AB]} value={Province[Province.AB]}>{langConfig.general.provinces.alb}</MenuItem>
                                <MenuItem key={Province[Province.BC]} value={Province[Province.BC]}>{langConfig.general.provinces.bc}</MenuItem>
                                <MenuItem key={Province[Province.YT]} value={Province[Province.YT]}>{langConfig.general.provinces.yuk}</MenuItem>
                                <MenuItem key={Province[Province.NT]} value={Province[Province.NT]}>{langConfig.general.provinces.nwt}</MenuItem>
                                <MenuItem key={Province[Province.NU]} value={Province[Province.NU]}>{langConfig.general.provinces.nun}</MenuItem>
                            </Select>
                            {provinceError && (
                                <Typography color="error" variant="caption">
                                    {langConfig.general.fieldInvalid}
                                </Typography>
                            )}
                        </FormControl>

                        <TextField
                            label={`${langConfig.register.p49}*`}
                            value={form.postalCode}
                            onChange={handlePostalCode}
                            fullWidth
                            margin="normal"
                            required
                            error={errors.postalCode}
                            helperText={errors.postalCode ? langConfig.general.fieldInvalid : ''}
                        />

                        <TextField
                            label={`${langConfig.register.p50}*`}
                            value={form.email}
                            onChange={(e) => {
                                logFieldChange('email', e.target.value, { previousValue: form.email });
                                const newForm = { ...form, email: e.target.value };
                                setForm(newForm);
                                validateField('email', e.target.value);
                                logFormState(newForm);
                            }}
                            fullWidth
                            margin="normal"
                            type="email"
                            required
                            error={errors.email}
                            helperText={errors.email ? langConfig.general.fieldInvalid : ''}
                        />

                        <TextField
                            label={`${langConfig.register.p51}*`}
                            type="password"
                            value={form.password}
                            onChange={(e) => {
                                logFieldChange('password', e.target.value, { previousValue: form.password });
                                const newForm = { ...form, password: e.target.value };
                                setForm(newForm);
                                validateField('password', e.target.value);
                                logFormState(newForm);
                            }}
                            fullWidth
                            margin="normal"
                            required
                            error={errors.password}
                            helperText={errors.password ? langConfig.general.fieldInvalid : ''}
                        />

                        <TextField
                            label={`${langConfig.register.p52}*`}
                            type="password"
                            value={form.password2}
                            onChange={(e) => {
                                logFieldChange('password2', e.target.value, { previousValue: form.password2 });
                                const newForm = { ...form, password2: e.target.value };
                                setForm(newForm);
                                validateField('password2', e.target.value);
                                logFormState(newForm);
                            }}
                            fullWidth
                            margin="normal"
                            required
                            error={errors.password2}
                            helperText={passwordError || (errors.password2 ? langConfig.general.fieldInvalid : '')}
                        />

                        <TextField
                            label={`${langConfig.register.p53}*`}
                            value={form.phoneNumber}
                            onChange={handlePhoneNumber}
                            fullWidth
                            margin="normal"
                            required
                            error={errors.phoneNumber}
                            helperText={errors.phoneNumber ? langConfig.general.fieldInvalid : ''}
                        />

                        <FormControl component="fieldset" sx={{ mt: 2 }} error={errors.preferredMethodOfContact}>
                            <Typography>{`${langConfig.register.p54}*`}</Typography>
                            <RadioGroup
                                value={form.preferredMethodOfContact}
                                onChange={(e) => {
                                    logFieldChange('preferredMethodOfContact', e.target.value, {
                                        previousValue: form.preferredMethodOfContact,
                                    });
                                    const newForm = { ...form, preferredMethodOfContact: e.target.value };
                                    setForm(newForm);
                                    validateField('preferredMethodOfContact', e.target.value);
                                    logFormState(newForm);
                                }}
                            >
                                <FormControlLabel value="EMAIL" control={<Radio />} label={langConfig.register.p55} />
                                <FormControlLabel value="PHONE" control={<Radio />} label={langConfig.register.p56} />
                            </RadioGroup>
                            {errors.preferredMethodOfContact && (
                                <Typography color="error" variant="caption">
                                    {langConfig.general.fieldInvalid}
                                </Typography>
                            )}
                        </FormControl>

                        <Typography variant="h6">{langConfig.register.p36}</Typography>
                        <Typography>* {langConfig.register.p37}</Typography>
                        <Typography>{langConfig.register.p38}</Typography>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                            <Button variant="outlined" onClick={handlePrevStep}>
                                {langConfig.general.goback}
                            </Button>
                            <Button variant="contained" onClick={handleSubmit}>
                                {langConfig.register.p57}
                            </Button>
                        </Box>
                    </div>
                </Box>
            )}


        </div>
    );

}

export default Register;